import React, { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Panel } from "@/components/Bits";
import {
  ShieldCheck, Plus, Trash2, Save, Lock, X, Users as UsersIcon, Info,
  AlertTriangle, Search,
} from "lucide-react";
import { toast } from "sonner";

const ipt = "w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/40";

export default function Roles() {
  const { user, refresh } = useAuth();
  const canManage = (user?.permissions || []).includes("roles.manage");
  const [perms, setPerms] = useState({ permissions: [], categories: [] });
  const [roles, setRoles] = useState([]);
  const [dirty, setDirty] = useState({}); // {roleCode: Set<permissionCode>}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    try {
      const [pr, rr] = await Promise.all([
        api.get("/permissions/registry"),
        api.get("/roles"),
      ]);
      setPerms(pr.data);
      setRoles(rr.data);
      setDirty({});
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Group permissions by category (must be before any conditional return)
  const grouped = useMemo(() => {
    const m = {};
    for (const p of perms.permissions) {
      if (!m[p.category]) m[p.category] = [];
      m[p.category].push(p);
    }
    return m;
  }, [perms.permissions]);

  if (!canManage) {
    return (
      <div className="p-8 text-slate-400 text-sm" data-testid="roles-forbidden">
        <ShieldCheck className="w-6 h-6 text-amber-400 mb-2" />
        Anda tidak punya permission <code>roles.manage</code>. Hanya admin yang bisa kelola role.
      </div>
    );
  }

  // Filtered permissions by search query
  const matches = (perm) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (perm.code + " " + perm.label + " " + (perm.description || "")).toLowerCase().includes(q);
  };

  const effectiveFor = (role) => {
    if (role.is_all_access) return new Set(perms.permissions.map((p) => p.code));
    const local = dirty[role.code];
    if (local) return local;
    return new Set(role.permissions || []);
  };

  const togglePerm = (roleCode, permCode) => {
    setDirty((d) => {
      const role = roles.find((r) => r.code === roleCode);
      const cur = new Set(d[roleCode] || role.permissions || []);
      if (cur.has(permCode)) cur.delete(permCode);
      else cur.add(permCode);
      return { ...d, [roleCode]: cur };
    });
  };

  const saveRole = async (roleCode) => {
    const perms_ = Array.from(dirty[roleCode] || []);
    setSaving(true);
    try {
      await api.patch(`/roles/${roleCode}`, { permissions: perms_ });
      toast.success(`Role '${roleCode}' updated`);
      await load();
      // Refresh own session so UI updates if my own role changed
      if (user?.role === roleCode) await refresh();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async (role) => {
    if (role.is_built_in) { toast.error("Role built-in tidak bisa dihapus"); return; }
    if (role.user_count > 0) { toast.error(`Reassign ${role.user_count} user dulu sebelum hapus role`); return; }
    if (!window.confirm(`Hapus role '${role.code}'? Aksi tidak bisa di-undo.`)) return;
    try {
      await api.delete(`/roles/${role.code}`);
      toast.success(`Role '${role.code}' dihapus`);
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const visiblePerms = perms.permissions.filter(matches);

  return (
    <div className="p-6 space-y-4 animate-slide-up" data-testid="roles-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan mb-1">System · Roles & Permissions</div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-cyan" /> Permission Management
          </h1>
          <p className="text-xs text-slate-400 mt-1 max-w-3xl">
            Atur akses per role. Admin selalu all-access (terkunci untuk mencegah lockout).
            Centang/uncheck permission di matriks → klik <b>Save</b> per kolom role.
            Bisa juga buat role custom baru.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="perm-search"
              placeholder="Cari permission…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={`${ipt} pl-8 w-56`}
            />
          </div>
          <button
            data-testid="new-role-btn"
            onClick={() => setShowNew(true)}
            className="bg-cyan hover:bg-cyan-dark text-bg font-semibold text-xs px-3 py-2 rounded-sm flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> New Role
          </button>
        </div>
      </div>

      {/* Roles header strip */}
      <div className="grid grid-cols-12 gap-3 text-xs">
        {roles.map((r) => {
          const hasDirty = !!dirty[r.code];
          return (
            <div key={r.code} className="col-span-12 md:col-span-4 lg:col-span-3 bg-surface border border-line rounded-sm p-3" data-testid={`role-card-${r.code}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  {r.is_all_access && <Lock className="w-3 h-3 text-amber-400" />}
                  <div className="font-bold text-sm text-cyan">{r.name}</div>
                </div>
                {!r.is_built_in && (
                  <button onClick={() => deleteRole(r)} className="text-rose-400 hover:text-rose-300" data-testid={`delete-role-${r.code}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-mono">{r.code}</div>
              <div className="text-[11px] text-slate-400 mt-1 line-clamp-2">{r.description || "—"}</div>
              <div className="flex items-center justify-between mt-2 text-[10px]">
                <span className="text-slate-400 flex items-center gap-1"><UsersIcon className="w-3 h-3" /> {r.user_count} user</span>
                <span className="text-slate-400">{effectiveFor(r).size} / {perms.permissions.length} perms</span>
              </div>
              {!r.is_all_access && hasDirty && (
                <button
                  data-testid={`save-role-${r.code}`}
                  onClick={() => saveRole(r.code)}
                  disabled={saving}
                  className="mt-2 w-full bg-cyan/20 hover:bg-cyan/30 text-cyan text-[11px] font-semibold py-1.5 rounded-sm flex items-center justify-center gap-1 transition"
                >
                  <Save className="w-3 h-3" /> Save changes
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Matrix */}
      <Panel title="Permission Matrix" testid="panel-matrix">
        {loading ? (
          <div className="p-6 text-center text-slate-500 text-sm">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="permissions-matrix">
              <thead className="border-b border-line sticky top-0 bg-surface z-10">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold w-[40%]">Permission</th>
                  {roles.map((r) => (
                    <th key={r.code} className="px-2 py-2 text-center font-mono text-[10px] uppercase tracking-wider" data-testid={`col-${r.code}`}>
                      {r.name}
                      {r.is_all_access && <Lock className="w-3 h-3 inline ml-1 text-amber-400" />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(perms.categories || []).map((cat) => {
                  const rows = (grouped[cat] || []).filter(matches);
                  if (!rows.length) return null;
                  return (
                    <React.Fragment key={cat}>
                      <tr className="bg-surface2">
                        <td colSpan={1 + roles.length} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-cyan font-bold">
                          {cat}
                        </td>
                      </tr>
                      {rows.map((p) => (
                        <tr key={p.code} className="border-b border-line/30 hover:bg-surface2/50" data-testid={`row-${p.code}`}>
                          <td className="px-3 py-2">
                            <div className="font-mono text-[11px] text-slate-200">{p.code}</div>
                            <div className="text-[10px] text-slate-500 mt-0.5">{p.description}</div>
                          </td>
                          {roles.map((r) => {
                            const eff = effectiveFor(r);
                            const checked = eff.has(p.code);
                            const locked = r.is_all_access;
                            return (
                              <td key={r.code} className="px-2 py-2 text-center">
                                <input
                                  data-testid={`cb-${r.code}-${p.code}`}
                                  type="checkbox"
                                  checked={checked}
                                  disabled={locked}
                                  onChange={() => togglePerm(r.code, p.code)}
                                  className="w-4 h-4 accent-cyan cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                {visiblePerms.length === 0 && (
                  <tr><td colSpan={1 + roles.length} className="px-3 py-8 text-center text-slate-500">Tidak ada permission yang cocok dengan pencarian.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Info note */}
      <div className="text-[11px] text-slate-500 bg-surface border border-line/50 rounded-sm p-3 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 text-cyan flex-shrink-0 mt-0.5" />
        <div className="space-y-1">
          <div><b className="text-slate-300">Catatan keamanan:</b> Perubahan permission berlaku langsung untuk semua user dengan role tersebut. User yang sudah login mungkin perlu refresh page agar UI menyesuaikan akses baru.</div>
          <div className="flex items-start gap-1"><AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5" /> Admin built-in <b>tidak bisa</b> di-edit atau dihapus — ini fail-safe agar Anda tidak terkunci dari sistem.</div>
        </div>
      </div>

      {showNew && (
        <NewRoleModal
          onClose={() => setShowNew(false)}
          permissions={perms.permissions}
          categories={perms.categories}
          grouped={grouped}
          onCreated={async () => { setShowNew(false); await load(); }}
        />
      )}
    </div>
  );
}

function NewRoleModal({ onClose, permissions, categories, grouped, onCreated }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const toggle = (c) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(c)) n.delete(c); else n.add(c);
      return n;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/roles", {
        code: code.trim().toLowerCase().replace(/\s+/g, "_"),
        name: name.trim(),
        description: description.trim(),
        permissions: Array.from(selected),
      });
      toast.success(`Role '${code}' dibuat`);
      onCreated();
    } catch (e2) {
      toast.error(formatErr(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-surface border border-line rounded-sm w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5 space-y-3" data-testid="new-role-modal">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2"><Plus className="w-4 h-4 text-cyan" /> Buat Role Baru</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">Code (unique slug)</label>
            <input data-testid="new-role-code" required value={code} onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))} placeholder="senior_agent" className={`${ipt} font-mono`} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">Display Name</label>
            <input data-testid="new-role-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Senior Agent" className={ipt} />
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">Description</label>
          <input data-testid="new-role-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Agent dengan akses lebih luas ke merchants & bulk edit" className={ipt} />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Permissions ({selected.size} dipilih)</label>
            <div className="flex gap-2 text-[10px]">
              <button type="button" onClick={() => setSelected(new Set(permissions.map((p) => p.code)))} className="text-cyan hover:underline">Pilih semua</button>
              <button type="button" onClick={() => setSelected(new Set())} className="text-slate-400 hover:underline">Kosongkan</button>
            </div>
          </div>
          <div className="border border-line rounded-sm divide-y divide-line/40 max-h-[40vh] overflow-y-auto">
            {(categories || []).map((cat) => (
              <div key={cat} className="p-2.5">
                <div className="text-[10px] uppercase tracking-wider text-cyan font-bold mb-1.5">{cat}</div>
                <div className="space-y-1">
                  {(grouped[cat] || []).map((p) => (
                    <label key={p.code} className="flex items-start gap-2 cursor-pointer hover:bg-surface2 p-1 rounded">
                      <input
                        type="checkbox"
                        data-testid={`new-role-perm-${p.code}`}
                        checked={selected.has(p.code)}
                        onChange={() => toggle(p.code)}
                        className="mt-0.5 w-3.5 h-3.5 accent-cyan"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-[11px] text-slate-200">{p.code}</div>
                        <div className="text-[10px] text-slate-500">{p.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 bg-bg border border-line py-2 rounded-sm text-sm hover:bg-surface2">Batal</button>
          <button type="submit" disabled={busy || !code || !name} data-testid="create-role-submit" className="flex-1 bg-cyan hover:bg-cyan-dark text-bg font-semibold py-2 rounded-sm text-sm disabled:opacity-50">
            {busy ? "Membuat..." : "Buat Role"}
          </button>
        </div>
      </form>
    </div>
  );
}
